import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Lock, UserPlus, X, Users } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { useToast } from '@/lib/hooks/use-toast';
import {
  quoteAccessService,
  type AccesoListado,
  type NivelDeAcceso,
  type TipoDeCotizacion,
} from '../services/quote-access.service';

interface Props {
  quoteId: string;
  /** Cuál de las tres tablas de cotizaciones. Por omisión, las normales. */
  tipo?: TipoDeCotizacion;
}

interface PersonaOEquipo {
  id: string;
  nombre: string;
}

/**
 * El bloque «Acceso» de la ficha: quién ve esta cotización y por qué.
 *
 * La cotización es privada de quien la creó. Todo lo demás que aparezca acá
 * está porque alguien apretó compartir, y por eso cada renglón dice de dónde
 * le viene el acceso en vez de mostrar un permiso suelto.
 */
export function PanelDeAcceso({ quoteId, tipo = 'QUOTE' }: Props) {
  const { toast } = useToast();
  const [accesos, setAccesos] = useState<AccesoListado[]>([]);
  /**
   * Si esta persona puede repartir accesos. Lo dice el servidor con la misma
   * función que después autoriza el POST, así que el botón no puede quedar
   * escondido cuando el permiso existe, ni ofrecido cuando no.
   *
   * Arranca en `false`: mientras no se sepa, no se ofrece. Ofrecer y que dé
   * 403 es peor que aparecer un instante después.
   */
  const [puedoCompartir, setPuedoCompartir] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [abriendo, setAbriendo] = useState(false);

  const [personas, setPersonas] = useState<PersonaOEquipo[]>([]);
  const [equipos, setEquipos] = useState<PersonaOEquipo[]>([]);
  const [aQuien, setAQuien] = useState<'persona' | 'equipo'>('persona');
  const [elegido, setElegido] = useState('');
  const [nivel, setNivel] = useState<NivelDeAcceso>('LECTURA');
  const [vence, setVence] = useState('');
  const [guardando, setGuardando] = useState(false);

  const traer = useCallback(async () => {
    try {
      const r = await quoteAccessService.listar(quoteId, tipo);
      setAccesos(r?.accesos ?? []);
      setPuedoCompartir(Boolean(r?.puedoCompartir));
    } catch {
      // Un error acá no puede tapar la cotización: se muestra el bloque vacío.
      setAccesos([]);
      setPuedoCompartir(false);
    } finally {
      setCargando(false);
    }
  }, [quoteId, tipo]);

  useEffect(() => {
    void traer();
  }, [traer]);

  const abrirFormulario = async () => {
    setAbriendo(true);
    if (personas.length > 0 || equipos.length > 0) return;
    try {
      const [us, orgs] = await Promise.all([
        apiClient.get<any[]>('/api/users'),
        apiClient.get<any[]>('/api/organizations'),
      ]);
      setPersonas(
        (us ?? []).map((u) => ({ id: u.id, nombre: `${u.firstName} ${u.lastName}`.trim() }))
      );
      setEquipos((orgs ?? []).map((o) => ({ id: o.id, nombre: o.name })));
    } catch {
      toast({
        title: 'No se pudo cargar la lista',
        description: 'Probá de nuevo en un momento.',
        variant: 'destructive',
      });
    }
  };

  const compartir = async () => {
    if (!elegido) return;
    setGuardando(true);
    try {
      await quoteAccessService.compartir(
        quoteId,
        {
          ...(aQuien === 'persona'
            ? { granteeUserId: elegido }
            : { granteeOrganizationId: elegido }),
          level: nivel,
          ...(vence ? { expiresAt: new Date(`${vence}T23:59:59`).toISOString() } : {}),
        },
        tipo
      );
      toast({ title: 'Listo', description: 'Ya tiene acceso a esta cotización.' });
      setElegido('');
      setVence('');
      setAbriendo(false);
      await traer();
    } catch (error: any) {
      toast({
        title: 'No se pudo compartir',
        description: error?.response?.data?.message ?? 'Revisá a quién elegiste.',
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  };

  const quitar = async (grantId: string, quien: string) => {
    try {
      await quoteAccessService.quitar(quoteId, grantId, tipo);
      toast({ title: 'Acceso quitado', description: `${quien} ya no la ve.` });
      await traer();
    } catch {
      toast({ title: 'No se pudo quitar el acceso', variant: 'destructive' });
    }
  };

  const opciones = (aQuien === 'persona' ? personas : equipos).map((p) => ({
    value: p.id,
    label: p.nombre,
  }));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Lock className="h-4 w-4" />
          Acceso
        </CardTitle>
        <CardDescription>
          Esta cotización es privada. La ve quien la creó y quien vos elijas.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {cargando ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : (
          <ul className="space-y-2">
            {accesos.map((a) => (
              <li
                key={a.id ?? 'dueno'}
                className="flex items-start justify-between gap-2 rounded-md border p-2"
              >
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 text-sm font-medium">
                    {a.origen === 'EQUIPO' && <Users className="h-3.5 w-3.5 shrink-0" />}
                    <span className="truncate">{a.quien}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">{a.porque}</p>
                  {a.vence && (
                    <p className="text-xs text-muted-foreground">
                      Hasta el {new Date(a.vence).toLocaleDateString('es-BO')}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Badge variant={a.nivel === 'EDICION' ? 'default' : 'secondary'}>
                    {a.nivel === 'EDICION' ? 'Puede editar' : 'Solo lectura'}
                  </Badge>
                  {puedoCompartir && a.sePuedeQuitar && a.id && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Quitar el acceso de ${a.quien}`}
                      onClick={() => quitar(a.id!, a.quien)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* El botón no existe si no podés compartir: no alcanza con deshabilitarlo. */}
        {puedoCompartir && !abriendo && (
          <Button variant="outline" className="w-full" onClick={abrirFormulario}>
            <UserPlus className="mr-2 h-4 w-4" />
            Dar acceso a alguien
          </Button>
        )}

        {puedoCompartir && abriendo && (
          <div className="space-y-2 rounded-md border p-3">
            <Select
              value={aQuien}
              onValueChange={(v) => {
                setAQuien(v as 'persona' | 'equipo');
                setElegido('');
              }}
            >
              <SelectTrigger aria-label="A quién">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="persona">A una persona</SelectItem>
                <SelectItem value="equipo">A un equipo</SelectItem>
              </SelectContent>
            </Select>

            <SearchableSelect
              options={opciones}
              value={elegido}
              onValueChange={setElegido}
              placeholder={aQuien === 'persona' ? 'Elegí a la persona' : 'Elegí el equipo'}
              searchPlaceholder="Buscar…"
              emptyMessage="No hay nadie con ese nombre"
            />

            <Select value={nivel} onValueChange={(v) => setNivel(v as NivelDeAcceso)}>
              <SelectTrigger aria-label="Qué puede hacer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="LECTURA">Solo lectura</SelectItem>
                <SelectItem value="EDICION">Puede editar</SelectItem>
              </SelectContent>
            </Select>

            <div>
              <label className="text-xs text-muted-foreground" htmlFor="vence-acceso">
                Hasta cuándo (opcional)
              </label>
              <Input
                id="vence-acceso"
                type="date"
                value={vence}
                onChange={(e) => setVence(e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={compartir} disabled={!elegido || guardando} className="flex-1">
                {guardando ? 'Compartiendo…' : 'Compartir'}
              </Button>
              <Button variant="ghost" onClick={() => setAbriendo(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PanelDeAcceso;
